/* ==========================================================================
   365 Flips — Collection page "2b" quick add
   - "+" on a card / "Add to Cart" on the featured card
   - single-variant → adds straight to the cart and opens the 1b drawer
   - multi-variant → <f365-quick-add> sheet with size / colour pills
   Depends on Dawn's global.js (fetchConfig) + the cart drawer (cart-drawer.js).
   ========================================================================== */
(() => {
  'use strict';

  const money = (cents) => {
    const el = document.querySelector('[data-money-format]');
    const fmt = (el && el.dataset.moneyFormat) || '${{amount}} USD';
    const amount = (Number(cents) || 0) / 100;
    const [i, d] = amount.toFixed(2).split('.');
    const grouped = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*amount\w*\s*\}\}/, `${grouped}.${d}`);
  };

  function readProduct(card) {
    const script = card.querySelector('script[data-f365-product]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('[365 collection] bad product JSON', e);
      return null;
    }
  }

  async function addToCart(variantId, quantity = 1) {
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'];
    const config = typeof fetchConfig === 'function' ? fetchConfig('json') : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
    config.body = JSON.stringify({
      items: [{ id: Number(variantId), quantity }],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(window.routes.cart_add_url, config).then((r) => r.json());
    if (response.status) throw new Error(response.description || response.message || 'Could not add to cart');
    if (drawer) {
      drawer.setActiveElement(document.activeElement);
      drawer.renderContents(response);
    } else if (response.sections && response.sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const fresh = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
    }
    return response;
  }

  function markAdded(card) {
    const btn = card.querySelector('[data-f365-quick-add]');
    if (!btn) return;
    btn.classList.remove('is-loading');
    btn.classList.add('is-added');
    const text = btn.querySelector('.f365-feat__btn-text');
    if (text) text.textContent = btn.dataset.addedLabel || 'Added ✓';
  }

  function setLoading(card, on) {
    const btn = card.querySelector('[data-f365-quick-add]');
    if (btn) btn.classList.toggle('is-loading', on);
  }

  /* ------------------------------------------------------------------
     Quick-add sheet
     ------------------------------------------------------------------ */
  class F365QuickAdd extends HTMLElement {
    connectedCallback() {
      this.img = this.querySelector('[data-f365-qa-img]');
      this.titleEl = this.querySelector('[data-f365-qa-title]');
      this.price = this.querySelector('[data-f365-qa-price]');
      this.options = this.querySelector('[data-f365-qa-options]');
      this.addBtn = this.querySelector('[data-f365-qa-add]');
      this.more = this.querySelector('[data-f365-qa-more]');
      this.querySelectorAll('[data-f365-qa-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addBtn.addEventListener('click', () => this.add());
      this.options.addEventListener('click', (e) => {
        const pill = e.target.closest('.f365-qa__pill');
        if (!pill) return;
        this.selected[Number(pill.dataset.index)] = pill.dataset.value;
        this.update();
      });
      document.addEventListener('keyup', (e) => e.key === 'Escape' && !this.hidden && this.close());
    }

    open(product, card) {
      this.product = product;
      this.card = card;
      const first = product.variants.find((v) => v.a) || product.variants[0];
      this.selected = first ? [...first.o] : [];
      this.titleEl.textContent = product.title;
      this.more.href = product.url;
      this.renderOptions();
      this.update();
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.addBtn.focus();
    }

    close() {
      this.hidden = true;
      document.body.classList.remove('overflow-hidden');
    }

    variantFor(sel) {
      return this.product.variants.find((v) => v.o.every((val, i) => val === sel[i]));
    }

    renderOptions() {
      this.options.innerHTML = this.product.options
        .map((opt, i) => {
          const pills = opt.v
            .map((value, j) => {
              const swatch = opt.s && opt.s[j];
              return `<button type="button" class="f365-qa__pill" data-index="${i}" data-value="${String(value).replace(/"/g, '&quot;')}">${
                swatch ? `<span class="f365-qa__dot" style="background:${swatch}"></span>` : ''
              }${value}</button>`;
            })
            .join('');
          return `<div class="f365-qa__group"><div class="f365-qa__group-label">${opt.n}</div><div class="f365-qa__pills">${pills}</div></div>`;
        })
        .join('');
    }

    update() {
      const p = this.product;
      const variant = this.variantFor(this.selected);
      // pill states
      this.options.querySelectorAll('.f365-qa__pill').forEach((pill) => {
        const i = Number(pill.dataset.index);
        const value = pill.dataset.value;
        pill.classList.toggle('is-selected', this.selected[i] === value);
        // unavailable if no available variant matches the other selections + this value
        const probe = [...this.selected];
        probe[i] = value;
        const v = this.variantFor(probe);
        pill.classList.toggle('is-unavailable', !v || !v.a);
      });
      const imgSrc = (variant && variant.img) || p.img;
      if (imgSrc) {
        this.img.src = imgSrc;
        this.img.alt = p.title;
      }
      if (variant) {
        this.price.innerHTML = `${variant.c > variant.p ? `<s>${money(variant.c)}</s>` : ''}${money(variant.p)}`;
        this.addBtn.disabled = !variant.a;
        this.addBtn.textContent = variant.a ? 'Add to Cart' : 'Sold out';
        this.variant = variant;
      } else {
        this.price.textContent = '';
        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Unavailable';
        this.variant = null;
      }
    }

    async add() {
      if (!this.variant || !this.variant.a) return;
      this.addBtn.classList.add('is-loading');
      this.addBtn.textContent = 'Adding…';
      try {
        await addToCart(this.variant.id);
        if (this.card) markAdded(this.card);
        this.close();
      } catch (e) {
        this.addBtn.textContent = e.message || 'Could not add';
        setTimeout(() => this.update(), 2500);
      } finally {
        this.addBtn.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('f365-quick-add')) customElements.define('f365-quick-add', F365QuickAdd);

  /* ------------------------------------------------------------------
     Card buttons
     ------------------------------------------------------------------ */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-f365-quick-add]');
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    const card = btn.closest('[data-f365-card]');
    const product = card && readProduct(card);
    if (!product) return;
    event.preventDefault();

    const available = product.variants.filter((v) => v.a);
    if (!available.length) {
      window.location.href = product.url;
      return;
    }

    if (product.variants.length === 1) {
      setLoading(card, true);
      try {
        await addToCart(available[0].id);
        markAdded(card);
      } catch (e) {
        console.error('[365 collection] add failed', e);
        setLoading(card, false);
      }
      return;
    }

    const sheet = document.querySelector('f365-quick-add');
    if (sheet) sheet.open(product, card);
    else window.location.href = product.url;
  });
})();
